import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { makeBranchApi } from "../../api/branchApi";
import {
  PageHeader, Btn, Badge, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle, TempPasswordBox,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export default function BranchStaffPage() {
  const { branchId } = useParams();
  const api = makeBranchApi(branchId);

  const [staff,    setStaff]   = useState([]);
  const [stations, setStations]= useState([]);
  const [loading,  setLoading] = useState(true);
  const [search,   setSearch]  = useState("");

  const [staffModal,  setStaffModal]  = useState(null);
  const [resetModal,  setResetModal]  = useState(null);
  const [confirmModal,setConfirmModal]= useState(null);
  const [saving,      setSaving]      = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, str] = await Promise.all([api.listStaff(), api.listStations()]);
      setStaff(sr.data?.results || sr.data || []);
      setStations(str.data?.results || str.data || []);
    } catch { toast.error("Failed to load staff."); }
    finally  { setLoading(false); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const filtered = staff.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const saveStaff = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await api.updateStaff(form.id, { name: form.name, station: form.station || null });
        toast.success("Staff updated.");
      } else {
        await api.createStaff({
          name: form.name, email: form.email,
          password: form.password, role: form.role || "staff",
          station: form.station || null,
          must_change_password: true,
          facility: branchId,
        });
        toast.success(`${form.name} added.`);
      }
      setStaffModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.email?.[0] || e.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  const handleResetPassword = async (newPw) => {
    setSaving(true);
    try {
      await api.resetStaffPassword(resetModal.id, { password: newPw });
      setResetResult({ password: newPw, email: resetModal.email });
      setResetModal(null);
      toast.success("Password reset.");
    } catch { toast.error("Failed to reset password."); }
    finally  { setSaving(false); }
  };

  const handleConfirmAction = async () => {
    if (!confirmModal) return;
    setSaving(true);
    try {
      const { type, user } = confirmModal;
      if (type === "deactivate") { await api.deactivateStaff(user.id); toast.success(`${user.name} deactivated.`); }
      if (type === "reactivate") { await api.reactivateStaff(user.id); toast.success(`${user.name} reactivated.`); }
      setConfirmModal(null); load();
    } catch { toast.error("Action failed."); }
    finally  { setSaving(false); }
  };

  const roleColor = (role) => {
    if (role === "facility_admin") return { bg: "rgba(96,165,250,0.12)", color: "#60A5FA", border: "rgba(96,165,250,0.25)" };
    return { bg: "rgba(34,197,94,0.1)", color: "#22C55E", border: "rgba(34,197,94,0.2)" };
  };

  if (loading) return <div style={{ padding: "32px" }}><LoadingSpinner /></div>;

  return (
    <div style={styles.root}>
      <PageHeader
        title="Staff"
        subtitle={`${staff.length} staff member${staff.length !== 1 ? "s" : ""} at this branch`}
        action={<Btn onClick={() => setStaffModal({ role: "staff" })}>+ Add Staff</Btn>}
      />

      <input
        style={styles.search}
        placeholder="Search staff…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {filtered.length === 0 ? (
        <EmptyState icon="👤" title="No staff yet"
          subtitle="Add the first staff member for this branch."
          action={<Btn onClick={() => setStaffModal({ role: "staff" })}>+ Add Staff</Btn>}
        />
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead><tr>
              {["Name", "Email", "Station", "Role", "Status", "Actions"].map(h =>
                <th key={h} style={styles.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {filtered.map(u => {
                const rc = roleColor(u.role);
                return (
                  <tr key={u.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={styles.avatar}>{u.name?.[0]?.toUpperCase() || "?"}</div>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#E2E8F0" }}>{u.name}</span>
                      </div>
                    </td>
                    <td style={{ ...styles.td, color: "#64748B", fontSize: "13px" }}>{u.email}</td>
                    <td style={{ ...styles.td, color: "#94A3B8", fontSize: "13px" }}>{u.station_name || "Unassigned"}</td>
                    <td style={styles.td}>
                      <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "10px", background: rc.bg, color: rc.color, border: `1px solid ${rc.border}`, fontWeight: "600" }}>
                        {u.role === "facility_admin" ? "Branch Admin" : "Staff"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <Badge value={u.is_active ? "active" : "inactive"} />
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <Btn size="sm" variant="ghost" onClick={() => setStaffModal({ ...u, station: u.station || "" })}>Edit</Btn>
                        <Btn size="sm" variant="ghost" onClick={() => setResetModal(u)}>Reset PW</Btn>
                        {u.is_active
                          ? <Btn size="sm" variant="danger" onClick={() => setConfirmModal({ type: "deactivate", user: u })}>Deactivate</Btn>
                          : <Btn size="sm" variant="success" onClick={() => setConfirmModal({ type: "reactivate", user: u })}>Reactivate</Btn>
                        }
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit staff modal */}
      <Modal
        open={!!staffModal}
        onClose={() => setStaffModal(null)}
        title={staffModal?.id ? "Edit Staff" : "Add Staff"}
        width="460px"
      >
        {staffModal && (
          <StaffForm
            form={staffModal}
            stations={stations}
            onSave={saveStaff}
            onClose={() => setStaffModal(null)}
            saving={saving}
          />
        )}
      </Modal>

      {/* Reset password modal */}
      <Modal open={!!resetModal} onClose={() => setResetModal(null)} title="Reset Password" width="400px">
        {resetModal && (
          <ResetPwForm
            user={resetModal}
            onReset={handleResetPassword}
            onClose={() => setResetModal(null)}
            saving={saving}
          />
        )}
      </Modal>

      {/* Reset result */}
      <Modal open={!!resetResult} onClose={() => setResetResult(null)} title="Password Reset ✓" width="420px">
        {resetResult && (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <TempPasswordBox password={resetResult.password} email={resetResult.email} />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn onClick={() => setResetResult(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!confirmModal} onClose={() => setConfirmModal(null)}
        onConfirm={handleConfirmAction} loading={saving}
        title={confirmModal?.type === "deactivate" ? "Deactivate Staff?" : "Reactivate Staff?"}
        message={confirmModal?.type === "deactivate"
          ? `${confirmModal?.user?.name} will lose access immediately.`
          : `${confirmModal?.user?.name} will regain access.`
        }
        confirmLabel={confirmModal?.type === "deactivate" ? "Deactivate" : "Reactivate"}
        variant={confirmModal?.type === "deactivate" ? "danger" : "success"}
      />
    </div>
  );
}

function StaffForm({ form, stations, onSave, onClose, saving }) {
  const [f, setF] = useState(form);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const isNew = !f.id;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <FormField label="Full Name" required>
        <input style={inputStyle} value={f.name || ""} onChange={e => set("name", e.target.value)} />
      </FormField>
      {isNew && (
        <>
          <FormField label="Email" required>
            <input style={inputStyle} type="email" value={f.email || ""} onChange={e => set("email", e.target.value)} />
          </FormField>
          <FormField label="Temporary Password" required>
            <input style={inputStyle} type="password" value={f.password || ""} onChange={e => set("password", e.target.value)} placeholder="Min. 8 characters" />
          </FormField>
          <FormField label="Role">
            <select style={selectStyle} value={f.role || "staff"} onChange={e => set("role", e.target.value)}>
              <option value="staff">Staff</option>
              <option value="facility_admin">Branch Admin</option>
            </select>
          </FormField>
        </>
      )}
      <FormField label="Assign to Station">
        <select style={selectStyle} value={f.station || ""} onChange={e => set("station", e.target.value)}>
          <option value="">Unassigned</option>
          {stations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </FormField>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn
          onClick={() => onSave(f)}
          disabled={saving || !f.name?.trim() || (isNew && (!f.email?.trim() || !f.password || f.password.length < 8))}
        >
          {saving ? "Saving…" : isNew ? "Add Staff" : "Save Changes"}
        </Btn>
      </div>
    </div>
  );
}

function ResetPwForm({ user, onReset, onClose, saving }) {
  const [pw, setPw] = useState(generateTempPw());
  const [mode, setMode] = useState("auto");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>
        Reset password for <strong style={{ color: "#E2E8F0" }}>{user.name}</strong>. They will be prompted to change it on next login.
      </p>
      <FormField label="Password">
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "#E2E8F0" }}>
            <input type="radio" checked={mode === "auto"} onChange={() => { setMode("auto"); setPw(generateTempPw()); }} />
            Auto-generate
          </label>
          <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "#E2E8F0" }}>
            <input type="radio" checked={mode === "manual"} onChange={() => setMode("manual")} />
            Set manually
          </label>
        </div>
      </FormField>
      {mode === "auto"
        ? <div style={{ fontFamily: "monospace", fontSize: "15px", color: "#60A5FA", background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.2)", borderRadius: "8px", padding: "10px 14px" }}>{pw}</div>
        : <input style={inputStyle} type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Min. 8 characters" />
      }
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onReset(pw)} disabled={saving || !pw || pw.length < 8}>{saving ? "Resetting…" : "Reset Password"}</Btn>
      </div>
    </div>
  );
}

function generateTempPw() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

const styles = {
  root:      { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1000px" },
  search:    { background: "rgba(30,41,59,0.8)", border: "1px solid rgba(51,65,85,0.8)", borderRadius: "8px", padding: "9px 14px", color: "#F1F5F9", fontSize: "14px", outline: "none", width: "300px", fontFamily: "inherit", marginBottom: "20px" },
  tableWrap: { background: "rgba(15,23,42,0.6)", border: "1px solid rgba(37,99,235,0.15)", borderRadius: "14px", overflow: "hidden" },
  table:     { width: "100%", borderCollapse: "collapse" },
  th:        { padding: "12px 16px", fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid rgba(37,99,235,0.1)", background: "rgba(15,23,42,0.8)" },
  tr:        { borderBottom: "1px solid rgba(37,99,235,0.06)" },
  td:        { padding: "12px 16px", verticalAlign: "middle" },
  avatar:    { width: "32px", height: "32px", borderRadius: "8px", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700", color: "#fff", flexShrink: 0 },
};
